import { IFilter } from "@saleor/components/Filter";
import { sectionNames } from "@saleor/intl";
import { ProductsUser } from "@saleor/products/customTypes/ProductsUser";
import useProductUserType from "@saleor/products/hooks/useProductUserType";
import { AutocompleteFilterOpts, FilterOpts } from "@saleor/types";
import {
  ProductStatusEnum,
  ProductUserViewEnum
} from "@saleor/types/globalTypes";
import {
  createAutocompleteField,
  createOptionsField
} from "@saleor/utils/filters/fields";
import { defineMessages, IntlShape } from "react-intl";

export enum ProductFilterKeys {
  attributes = "attributes",
  categories = "categories",
  productStatus = "productStatus",
  productVisibility = "productVisibility",
  productInventory = "productInventory"
}

export interface ProductListFilterOpts {
  categories: FilterOpts<string[]> & AutocompleteFilterOpts;
  productStatus: FilterOpts<ProductStatusEnum>;
  productVisibility: FilterOpts<ProductVisibility>;
  productInventory: FilterOpts<ProductInventory>;
}

export enum ProductInventory {
  INSTOCK = "inStock",
  OUTOFSTOCK = "outOfStock"
}

export enum ProductStatus {
  DRAFT = "draft",
  READY = "ready",
  UNLISTED = "unlisted"
}

export enum ProductVisibility {
  PUBLISHED = "published",
  UNPUBLISHED = "unpublished"
}

const messages = defineMessages({
  available: {
    defaultMessage: "Available",
    description: "product status"
  },
  draft: {
    defaultMessage: "Draft",
    description: "product status"
  },
  hidden: {
    defaultMessage: "Hidden",
    description: "product is hidden"
  },
  inStock: {
    defaultMessage: "In-Stock",
    description: "inStock"
  },
  outOfStock: {
    defaultMessage: "Out Of Stock",
    description: "product status"
  },
  price: {
    defaultMessage: "Price"
  },
  published: {
    defaultMessage: "Published",
    description: "product visibility"
  },
  productInventory: {
    defaultMessage: "Product Inventory",
    description: "product Inventory"
  },
  quantity: {
    defaultMessage: "Stock quantity",
    description: "product"
  },
  ready: {
    defaultMessage: "Ready",
    description: "product status"
  },
  status: {
    defaultMessage: "Product Status",
    description: "product status"
  },
  unlisted: {
    defaultMessage: "Unlisted",
    description: "product status"
  },
  unpublished: {
    defaultMessage: "Unpublished",
    description: "product visibility"
  },
  visibility: {
    defaultMessage: "Visibility",
    description: "product visibility"
  },
  visible: {
    defaultMessage: "Visible",
    description: "product is visible"
  }
});

function getFilterStructureByUserType(
  intl: IntlShape,
  opts: ProductListFilterOpts,
  user: ProductsUser
) {
  const isKAMSStoreAdmin =
    user.isKAMS && user.userType === ProductUserViewEnum.STORE_ADMIN;
  const isKAMSSellerMaster =
    user.isKAMS && user.userType === ProductUserViewEnum.SELLER_MASTER;

  if (isKAMSStoreAdmin) {
    return [
      {
        ...createOptionsField(
          ProductFilterKeys.productVisibility,
          intl.formatMessage(messages.visibility),
          [opts.productVisibility.value],
          false,
          [
            {
              label: intl.formatMessage(messages.published),
              value: ProductVisibility.PUBLISHED
            },
            {
              label: intl.formatMessage(messages.unpublished),
              value: ProductVisibility.UNPUBLISHED
            }
          ]
        ),
        active: opts.productVisibility.active
      }
    ];
  } else if (isKAMSSellerMaster) {
    return [
      {
        ...createOptionsField(
          ProductFilterKeys.productStatus,
          intl.formatMessage(messages.status),
          [opts.productStatus.value],
          false,
          [
            {
              label: intl.formatMessage(messages.draft),
              value: ProductStatusEnum.DRAFT
            },
            {
              label: intl.formatMessage(messages.ready),
              value: ProductStatusEnum.READY
            },
            {
              label: intl.formatMessage(messages.unlisted),
              value: ProductStatusEnum.UNLISTED
            }
          ]
        ),
        active: opts.productStatus.active
      }
    ];
  } else {
    return getDefaultFilterStructure(intl, opts, user);
  }
}

function getDefaultFilterStructure(
  intl: IntlShape,
  opts: ProductListFilterOpts,
  user: ProductsUser
) {
  const defaultSort = [
    {
      ...createAutocompleteField(
        ProductFilterKeys.categories,
        intl.formatMessage(sectionNames.categories),
        opts.categories.value,
        opts.categories.displayValues,
        true,
        opts.categories.choices,
        {
          hasMore: opts.categories.hasMore,
          initialSearch: "",
          loading: opts.categories.loading,
          onFetchMore: opts.categories.onFetchMore,
          onSearchChange: opts.categories.onSearchChange
        }
      ),
      active: opts.categories.active
    }
  ];

  if (user.userType === ProductUserViewEnum.STORE_ADMIN) {
    return [
      {
        ...createOptionsField(
          ProductFilterKeys.productVisibility,
          intl.formatMessage(messages.visibility),
          [opts.productVisibility.value],
          false,
          [
            {
              label: intl.formatMessage(messages.published),
              value: ProductVisibility.PUBLISHED
            },
            {
              label: intl.formatMessage(messages.unpublished),
              value: ProductVisibility.UNPUBLISHED
            }
          ]
        ),
        active: opts.productVisibility.active
      },
      {
        ...createOptionsField(
          ProductFilterKeys.productInventory,
          intl.formatMessage(messages.productInventory),
          [opts.productInventory.value],
          false,
          [
            {
              label: intl.formatMessage(messages.inStock),
              value: ProductInventory.INSTOCK
            },
            {
              label: intl.formatMessage(messages.outOfStock),
              value: ProductInventory.OUTOFSTOCK
            }
          ]
        ),
        active: opts.productInventory.active
      },
      ...defaultSort
    ];
  }

  return [
    {
      ...createOptionsField(
        ProductFilterKeys.productStatus,
        intl.formatMessage(messages.status),
        [opts.productStatus.value],
        false,
        [
          {
            label: intl.formatMessage(messages.draft),
            value: ProductStatusEnum.DRAFT
          },
          {
            label: intl.formatMessage(messages.ready),
            value: ProductStatusEnum.READY
          },
          {
            label: intl.formatMessage(messages.unlisted),
            value: ProductStatusEnum.UNLISTED
          }
        ]
      ),
      active: opts.productStatus.active
    },
    ...defaultSort
  ];
}

export function createFilterStructure(
  intl: IntlShape,
  opts: ProductListFilterOpts,
  params: any
): IFilter<ProductFilterKeys> {
  const user = useProductUserType(params);
  return getFilterStructureByUserType(intl, opts, user);
}
